/**
 * MCP tool result helpers + argument coercion.
 * Every in-app tool handler returns the uniform BridgeToolResult shape so
 * the relay can translate it 1:1 into MCP content blocks.
 */

import type { BridgeToolResult } from './mcpBridgeProtocol';

export type McpToolHandler = (args: Record<string, unknown>) => Promise<BridgeToolResult> | BridgeToolResult;

export function okText(text: string): BridgeToolResult {
    return { ok: true, text };
}

export function okJson(data: unknown, text?: string): BridgeToolResult {
    return { ok: true, data, text };
}

export function okImage(imageBase64: string, text: string): BridgeToolResult {
    return { ok: true, imageBase64, text };
}

export function fail(error: string): BridgeToolResult {
    return { ok: false, error };
}

// ---------------------------------------------------------------------------
// Argument coercion (the relay forwards arbitrary agent JSON)
// ---------------------------------------------------------------------------

export function asString(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

export function asStringDefault(value: unknown, fallback: string): string {
    return asString(value) ?? fallback;
}

export function asNumber(value: unknown): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
        const n = Number(value);
        return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
}

export function asBoolean(value: unknown): boolean | undefined {
    if (typeof value === 'boolean') return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return undefined;
}

export function asStringArray(value: unknown): string[] | undefined {
    if (!Array.isArray(value)) return undefined;
    const out = value.filter((v): v is string => typeof v === 'string');
    return out;
}

/** Parse a numeric triplet like [x, y, z] from an array of strings/numbers. */
export function asVec3(value: unknown): [number, number, number] | undefined {
    const arr = Array.isArray(value)
        ? value
        : typeof value === 'string'
            ? value.split(/[\s,]+/)
            : null;
    if (!arr || arr.length < 3) return undefined;
    const nums = arr.slice(0, 3).map((v) => asNumber(v));
    if (nums.some((n) => n === undefined)) return undefined;
    return [nums[0] as number, nums[1] as number, nums[2] as number];
}

// ---------------------------------------------------------------------------
// Deep-safe JSON for element/entity payloads (THREE objects, Maps, circulars)
// ---------------------------------------------------------------------------

/**
 * JSON.stringify that survives live model objects: functions and THREE
 * internals (BufferGeometry, Object3D, typed arrays, Maps) collapse to
 * descriptive placeholders instead of crashing or dumping megabytes.
 */
export function safeJsonStringify(value: unknown, space = 0): string {
    const seen = new WeakSet<object>();
    return JSON.stringify(
        value,
        function replacer(_key: string, val: unknown) {
            if (val === null || val === undefined) return val;
            if (typeof val === 'function') return undefined;
            if (typeof val !== 'object') return val;
            const obj = val as Record<string, unknown>;
            if (seen.has(obj as object)) return '[Circular]';
            seen.add(obj as object);
            if (obj.isBufferGeometry === true) return '[BufferGeometry]';
            if (obj.isObject3D === true) return '[Object3D]';
            if (obj.isMaterial === true) return '[Material]';
            if (obj.isTexture === true) return '[Texture]';
            if (typeof ArrayBuffer !== 'undefined' &&
                (val instanceof ArrayBuffer || ArrayBuffer.isView(val))) {
                return `[binary ${(val as ArrayBufferView).byteLength ?? 0}B]`;
            }
            if (val instanceof Map) return `[Map size=${val.size}]`;
            if (val instanceof Set) return `[Set size=${val.size}]`;
            return val;
        },
        space,
    );
}
